import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlarmClock, ArrowLeft, CircleAlert, Mail, MessageCircle, NotebookPen, Phone, RefreshCw, Video } from "lucide-react";

import StagePage from "src/stage/StagePage";
import { WsSegmented } from "src/stage/WsControls";
import { Skeleton, formatDay, formatNumber } from "src/stage/StageUi";
import { PersonAvatar } from "./TeamDirectory";
import { useWorkspace } from "../context/WorkspaceContext";
import { getTeamMemberPerformance } from "../utils/teamMembersApi";
import { getErrorMessage } from "../services/workspaceService";
import "src/features/Stage/home.css";
import "./team-stage.css";

const RANGES = [{ id: "7", label: "7 days" }, { id: "30", label: "30 days" }, { id: "90", label: "90 days" }];
const CHANNELS = [
  { key: "calls", label: "Calls", icon: Phone },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { key: "emails", label: "Emails", icon: Mail },
  { key: "meetings", label: "Meetings", icon: Video },
  { key: "followups", label: "Follow-ups", icon: AlarmClock },
  { key: "notes", label: "Notes", icon: NotebookPen },
];
const TYPE_LABELS = { CALL: "Call", EMAIL: "Email", WHATSAPP: "WhatsApp", MEETING: "Meeting", NOTE: "Note", FOLLOWUP: "Follow-up" };
const dayKey = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

// The API only returns days with activity; fill the rest so the chart keeps its rhythm.
function fillDays(rows, days) {
  const byDate = new Map((rows || []).map((row) => [row.date, row]));
  const out = [];
  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const date = new Date();
    date.setDate(date.getDate() - offset);
    const key = dayKey(date);
    out.push({ date: key, activities: Number(byDate.get(key)?.activities ?? byDate.get(key)?.total ?? 0) });
  }
  return out;
}

export default function TeamMemberPerformance() {
  const { userId } = useParams();
  const { currentWorkspaceId } = useWorkspace();
  const [range, setRange] = useState("30");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!currentWorkspaceId || !userId) return;
    setLoading(true);
    setError(null);
    try {
      const from = new Date();
      from.setDate(from.getDate() - (Number(range) - 1));
      setData(await getTeamMemberPerformance(currentWorkspaceId, userId, { from: dayKey(from), to: dayKey(new Date()) }));
    } catch (err) {
      setError(getErrorMessage(err, "Unable to load team performance."));
    } finally {
      setLoading(false);
    }
  }, [currentWorkspaceId, range, userId]);

  useEffect(() => { load(); }, [load]);

  const kpis = data?.kpis || {};
  const member = data?.member;
  const daily = useMemo(() => fillDays(data?.daily_activity, Number(range)), [data, range]);
  const busiest = Math.max(0, ...daily.map((day) => day.activities));
  const peak = Math.max(1, busiest);
  const channelMax = Math.max(1, ...CHANNELS.map((channel) => Number(kpis[channel.key]) || 0));

  return (
    <StagePage
      className="crm-team-page"
      eyebrow={<Link to="/teams" className="crm-back-link"><ArrowLeft size={13} />Team</Link>}
      title={member?.name || "Team member"}
      subtitle={member ? [member.email, member.role && `${member.role[0].toUpperCase()}${member.role.slice(1)}`, member.joined_at && `joined ${new Date(member.joined_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" })}`].filter(Boolean).join(" · ") : "How this person is working their leads."}
      actions={(
        <>
          {member && <span className="crm-perf-person"><PersonAvatar name={member.name} size={34} status={member.status === "invited" ? "pending" : member.status} /><span className={`ws-status ws-status--${member.status === "active" ? "live" : "draft"}`}>{member.status === "active" ? "Active" : member.status}</span></span>}
          <WsSegmented label="Period" value={range} onChange={setRange} items={RANGES} />
          <button type="button" className="crm-icon-btn" onClick={load} disabled={loading} aria-label="Refresh" title="Refresh"><RefreshCw size={16} className={loading ? "ws-spin" : ""} /></button>
        </>
      )}
    >
      {error ? (
        <div className="ws-notice" role="alert"><CircleAlert size={16} style={{ flex: "none", marginTop: 1 }} /><span className="ws-grow">{error}</span><button type="button" className="ws-btn ws-btn--quiet ws-btn--sm" onClick={load}>Try again</button></div>
      ) : (
        <>
          <section className="ws-card ws-kpis crm-perf-kpis ws-rise" aria-label="Performance summary">
            <div className="ws-kpi"><span className="ws-kpi-label">Leads owned</span><span className="ws-kpi-value">{loading ? "—" : formatNumber(kpis.assigned_leads || 0)}</span><span className="ws-muted">Assigned right now</span></div>
            <div className="ws-kpi"><span className="ws-kpi-label">Leads worked</span><span className="ws-kpi-value">{loading ? "—" : formatNumber(kpis.interacted_leads || 0)}</span><span className="ws-muted">With at least one activity</span></div>
            <div className="ws-kpi"><span className="ws-kpi-label">Activities</span><span className="ws-kpi-value">{loading ? "—" : formatNumber(kpis.total_activities || 0)}</span>{!loading && <div className="ws-meter" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={kpis.activity_completion_rate || 0} aria-label="Completed"><span style={{ width: `${kpis.activity_completion_rate || 0}%` }} /></div>}<span className="ws-muted">{loading ? "" : `${kpis.activity_completion_rate || 0}% completed`}</span></div>
            <div className="ws-kpi"><span className="ws-kpi-label">Pipeline steps done</span><span className="ws-kpi-value">{loading ? "—" : formatNumber(kpis.nodes_completed || 0)}</span><span className="ws-muted">{loading ? "" : `${formatNumber(kpis.nodes_skipped || 0)} skipped`}</span></div>
          </section>

          <div className="crm-perf-grid">
            <section className="ws-card ws-card-pad ws-rise">
              <h2 className="ws-h3">Activity per day</h2>
              <p className="ws-muted">{loading ? "Loading…" : busiest ? `Busiest day: ${formatNumber(busiest)} activities` : "No activity in this period"}</p>
              {loading ? <Skeleton height={150} radius={12} style={{ marginTop: 10 }} /> : (
                <>
                  <div className="crm-perf-bars" role="img" aria-label={`Activities per day for the last ${range} days`}>
                    {daily.map((day) => <span key={day.date} className={day.activities ? "" : "is-zero"} style={{ height: `${(day.activities / peak) * 100}%` }} title={`${formatDay(`${day.date}T12:00:00`)}: ${day.activities} ${day.activities === 1 ? "activity" : "activities"}`} />)}
                  </div>
                  <div className="crm-perf-axis"><span>{formatDay(`${daily[0]?.date}T12:00:00`)}</span><span>Today</span></div>
                </>
              )}
            </section>
            <section className="ws-card ws-card-pad ws-rise">
              <h2 className="ws-h3">How they reach out</h2>
              <p className="ws-muted">Activities by type in this period</p>
              <div className="crm-perf-channels">
                {CHANNELS.map(({ key, label, icon: Icon }) => (
                  <div key={key} className="crm-perf-channel">
                    <span className="ws-icon-tile crm-home-tile" aria-hidden="true" style={{ width: 32, height: 32 }}><Icon size={15} /></span>
                    <div className="ws-grow"><b>{loading ? "—" : formatNumber(kpis[key] || 0)}</b><span>{label}</span>
                      {!loading && <div className="ws-meter" style={{ height: 4 }}><span style={{ width: `${((Number(kpis[key]) || 0) / channelMax) * 100}%` }} /></div>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className="ws-card ws-card-pad ws-rise">
            <h2 className="ws-h3">Recent interactions</h2>
            <p className="ws-muted">Calls, notes, follow-ups and meetings this person logged.</p>
            {loading ? <Skeleton height={160} radius={12} style={{ marginTop: 10 }} /> : !data?.recent_activity?.length ? <div className="ws-empty" style={{ marginTop: 10 }}>No activity recorded in this period.</div> : (
              <ul className="crm-home-list">
                {data.recent_activity.map((activity) => (
                  <li key={activity.id} className="crm-home-person">
                    <span className="ws-grow">
                      <span className="crm-home-row-title">{TYPE_LABELS[activity.type] || activity.type} · {activity.lead?.name || "Lead"}</span>
                      <span className="crm-home-row-sub">{activity.note || "No note added"}</span>
                    </span>
                    <span className={`ws-status ws-status--${activity.status === "COMPLETED" ? "live" : activity.status === "PENDING" ? "info" : "draft"}`}>{activity.status === "COMPLETED" ? "Done" : activity.status === "PENDING" ? "Pending" : "Cancelled"}</span>
                    <span className="crm-home-when" style={{ textAlign: "right" }}><span>{formatDay(activity.created_at)}</span></span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </StagePage>
  );
}
